import { Alert, Container, ListItem, ListItemText, Paper } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PluginsForm } from '../plugins-form'
import { SectionHeader } from '@sitoo/mui-components'
import { SwitchInput } from '../../../../inputs/switch-input'
import {
  GetTaxFreePluginDocument,
  UpdateTaxFreeDocument,
  PluginTaxFree,
  GetTaxFreePluginAuthDocument,
  UpdateTaxFreeAuthDocument,
  PluginTaxFreeAuth,
} from '../../../../generated/graphql'
import { useMutation, useQuery } from '@apollo/client'
import { useMe } from '../../../../hooks/me'
import { DEFAULT_SITE_ID } from '../../../../utils/constants'
import { AuthenticationSection } from '../authentication/section'
import { useState } from 'react'
import {
  AuthenticationDialog,
  AuthenticationDialogForm,
} from '../authentication/dialog'

type Form = PluginTaxFree

const validateTaxFreeAuth = (plugin: PluginTaxFreeAuth | undefined) => {
  return plugin && Object.values(plugin).every(Boolean)
}

const toUrl = (url: string) => {
  try {
    return new URL(url)
  } catch {
    return
  }
}

export const SettingsTaxFreePage = () => {
  const { t } = useTranslation(['shared', 'apps'])

  const { siteId } = useMe()
  const isDefaultSite = siteId === DEFAULT_SITE_ID

  const formContext = useForm<Form>({
    defaultValues: { active: false },
  })

  const { control, formState } = formContext

  const { data, loading: fetchLoading } = useQuery(GetTaxFreePluginDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const { data: authData, loading: authLoading } = useQuery(
    GetTaxFreePluginAuthDocument,
    { fetchPolicy: 'cache-and-network' },
  )

  const [isAuthOpen, setAuthOpen] = useState(false)
  const isAuthConfigured = Boolean(
    authData?.taxFreePluginAuth.api_url &&
      authData?.taxFreePluginAuth.api_token,
  )

  const [updateSettings, { loading: updateLoading }] = useMutation(
    UpdateTaxFreeDocument,
  )

  const [updateAuth, { loading: updateAuthLoading }] = useMutation(
    UpdateTaxFreeAuthDocument,
    {
      refetchQueries: [GetTaxFreePluginAuthDocument],
      awaitRefetchQueries: true,
    },
  )

  const isLoading =
    fetchLoading || updateLoading || updateAuthLoading || authLoading

  const isAuthValid =
    isLoading || validateTaxFreeAuth(authData?.taxFreePluginAuth)

  const onSubmit = async (formData: Form) => {
    const { data } = await updateSettings({ variables: { data: formData } })

    return data?.updateTaxFree
  }

  const onAuthSubmit = async (formData: AuthenticationDialogForm) => {
    const { login, password } = formData

    await updateAuth({
      variables: { data: { api_url: login, api_token: password } },
    })
  }

  return (
    <Container>
      <AuthenticationDialog
        open={isAuthOpen}
        onClose={() => setAuthOpen(false)}
        onSubmit={onAuthSubmit}
        defaultValues={{
          login: authData?.taxFreePluginAuth.api_url,
        }}
        slotProps={{
          loginInput: {
            label: t('apps:tax_free.api_url_label'),
            helperText: t('apps:tax_free.api_url_helper'),
            rules: {
              invalidUrl: (value: string) =>
                value && !toUrl(value)
                  ? t('apps:tax_free.invalid_url')
                  : undefined,
              endsWithSlash: (value: string) =>
                value && !value.endsWith('/')
                  ? t('apps:tax_free.invalid_url_slash')
                  : undefined,
            },
          },
          passwordInput: {
            label: t('apps:tax_free.api_token_label'),
            helperText: t('apps:tax_free.api_token_helper'),
          },
        }}
      />

      <FormProvider {...formContext}>
        <PluginsForm
          serverData={data?.taxFreePlugin}
          onSubmit={onSubmit}
          submitLabel={t('shared:action.save')}
          isLoading={isLoading}
        >
          <Paper sx={{ mb: 2 }}>
            <SectionHeader variant="transparent">
              {t('apps:tax_free.settings_label')}
            </SectionHeader>

            {!isAuthValid && (
              <Alert color="error" variant="outlined" sx={{ mx: 2, my: 1 }}>
                {siteId === DEFAULT_SITE_ID
                  ? t('apps:tax_free.auth_required')
                  : t('apps:tax_free.auth_required_franchise')}
              </Alert>
            )}

            <ListItem
              component="div"
              secondaryAction={
                <SwitchInput
                  control={control}
                  name="active"
                  aria-label={t('apps:tax_free.active_label')}
                />
              }
            >
              <ListItemText
                primary={t('apps:tax_free.active_label')}
                secondary={t('apps:tax_free.active_helper')}
              />
            </ListItem>
          </Paper>

          {isDefaultSite && (
            <AuthenticationSection
              isConfigured={isAuthConfigured}
              disabled={formState.isDirty}
              onClick={() => setAuthOpen(true)}
              isLoading={isLoading}
            />
          )}
        </PluginsForm>
      </FormProvider>
    </Container>
  )
}
